import { PAYS_BY } from '../constants/countries';

/**
 * Taux de TVA de l'entreprise : les trois taux légaux, leur historique et le
 * taux par défaut des documents.
 *
 * <p>Les taux changent avec les années : 7,7 % jusqu'à fin 2023, 8,1 % depuis.
 * Ils étaient écrits en dur dans une dizaine d'écrans, et un changement de loi
 * aurait demandé une nouvelle version de l'application — quand il doit se
 * régler en une minute dans Réglages. Tout ce qui a besoin d'un taux le
 * demande ici, pour une date : un devis daté de 2023 propose 7,7 %, un ticket
 * de caisse d'aujourd'hui 8,1 %.
 *
 * <p>Un document conserve le taux qu'il porte (`tvaPct`, taux de ligne) une
 * fois créé : ce qui se lit ici ne vaut que pour les nouvelles pièces et pour
 * ce qui se recalcule à une date — dépenses, décompte.
 */

/** Les trois taux légaux (LTVA art. 25), tels que l'écran les nomme. */
export const TYPES_TVA = [
  { cle: 'normal', libelle: 'Taux normal', detail: 'prestations et marchandises ordinaires' },
  {
    cle: 'reduit',
    libelle: 'Taux réduit',
    detail: 'denrées alimentaires, boissons sans alcool, livres, médicaments',
  },
  {
    cle: 'hebergement',
    libelle: 'Taux spécial hébergement',
    detail: 'nuitées, petit-déjeuner compris',
  },
];

/** Les taux suisses et leur date d'entrée en vigueur, du plus ancien au plus récent. */
export const HISTORIQUE_LEGAL_SUISSE = [
  { des: '2018-01-01', normal: 7.7, reduit: 2.5, hebergement: 3.7 },
  { des: '2024-01-01', normal: 8.1, reduit: 2.6, hebergement: 3.8 },
];

/**
 * Les taux légaux suisses les plus récents : ce que retient une pièce dont on
 * ne connaît ni le taux ni la date.
 */
export const TAUX_LEGAUX_ACTUELS = HISTORIQUE_LEGAL_SUISSE[HISTORIQUE_LEGAL_SUISSE.length - 1];

/** Clé « AAAA-MM-JJ » d'une date : un objet Date, « AAAA-MM-JJ » ou « JJ.MM.AAAA ». */
export function cleDate(date) {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const jj = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${jj}`;
  }
  const texte = String(date ?? '').trim();
  const iso = texte.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const suisse = texte.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (suisse) return `${suisse[3]}-${suisse[2].padStart(2, '0')}-${suisse[1].padStart(2, '0')}`;
  return cleDate(new Date());
}

/** Un taux saisi (« 8,1 », 8.1), entre 0 et 100 ; `null` sinon. */
export function tauxLu(valeur) {
  if (valeur === '' || valeur == null || typeof valeur === 'boolean') return null;
  const n = Number(String(valeur).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

/** Une période réglée, si ses trois taux et sa date se lisent ; `null` sinon. */
function periodeLue(periode) {
  if (!periode || typeof periode !== 'object') return null;
  const des = String(periode.des ?? '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(des) || Number.isNaN(new Date(des).getTime())) return null;
  const normal = tauxLu(periode.normal);
  const reduit = tauxLu(periode.reduit);
  const hebergement = tauxLu(periode.hebergement);
  if (normal == null || reduit == null || hebergement == null) return null;
  return { des, normal, reduit, hebergement };
}

/**
 * L'historique quand rien n'est réglé : la loi suisse, ou les taux du pays
 * enregistré, sans date, pour une entreprise établie ailleurs.
 */
function historiqueParDefaut(soc) {
  const pays = soc?.pays || 'CH';
  if (pays === 'CH' || pays === 'LI') return HISTORIQUE_LEGAL_SUISSE;
  const taux = (PAYS_BY[pays]?.tva || []).filter((t) => t > 0).sort((a, b) => b - a);
  if (!taux.length) return HISTORIQUE_LEGAL_SUISSE;
  return [
    { des: '', normal: taux[0], reduit: taux[taux.length - 1], hebergement: taux[1] ?? taux[0] },
  ];
}

/**
 * L'historique des taux de l'entreprise, du plus ancien au plus récent.
 *
 * <p>Une période mal écrite — date illisible, taux absent ou hors de 0 à
 * 100 — est ignorée ; sans aucune période valable, la loi s'applique.
 *
 * @param {object} [soc] entreprise
 * @returns {Array<{des: string, normal: number, reduit: number, hebergement: number}>}
 *          les périodes, triées par date d'entrée en vigueur
 */
export function historiqueTva(soc) {
  const regle = Array.isArray(soc?.tvaHistorique) ? soc.tvaHistorique : [];
  const periodes = regle.map(periodeLue).filter(Boolean);
  if (!periodes.length) return historiqueParDefaut(soc);
  return periodes.sort((a, b) => a.des.localeCompare(b.des));
}

/**
 * Les taux en vigueur à une date : la dernière période entrée en vigueur à
 * cette date, ou la plus ancienne connue pour une date antérieure à toutes.
 *
 * @param {object} [soc] entreprise
 * @param {Date|string} [date] date de la pièce ; aujourd'hui par défaut
 * @returns {{des: string, normal: number, reduit: number, hebergement: number}}
 *          les trois taux et la date depuis laquelle ils valent
 */
export function tauxTvaEnVigueur(soc, date) {
  const cle = cleDate(date);
  const periodes = historiqueTva(soc);
  let retenue = periodes[0];
  for (const periode of periodes) {
    if (periode.des <= cle) retenue = periode;
  }
  return { ...retenue };
}

/**
 * Le taux d'un type à une date : 0 pour une pièce exonérée ou sans type.
 *
 * @param {object} [soc] entreprise
 * @param {string|null} type `normal`, `reduit`, `hebergement`, ou rien
 * @param {Date|string} [date] date de la pièce ; aujourd'hui par défaut
 * @returns {number} le taux, en pour-cent
 */
export function tauxTvaDeType(soc, type, date) {
  if (!TYPES_TVA.some((t) => t.cle === type)) return 0;
  return tauxTvaEnVigueur(soc, date)[type];
}

/**
 * Type de taux appliqué par défaut aux nouveaux documents : celui réglé dans
 * Réglages → Documents, le taux normal à défaut.
 *
 * <p>Un réglage ancien, écrit en nombre libre, a été saisi sous les taux de
 * son époque — que ceux d'aujourd'hui approchent le mieux : il est rattaché au
 * type qui porte ce taux aujourd'hui, et ce type suit ensuite la loi.
 *
 * @param {object} [soc] entreprise
 * @returns {'normal'|'reduit'|'hebergement'|'exonere'} le type
 */
export function typeTvaDefaut(soc) {
  const regle = soc?.tvaDefaut;
  if (regle === 'exonere' || TYPES_TVA.some((t) => t.cle === regle)) return regle;
  const n = tauxLu(regle);
  if (n === 0) return 'exonere';
  if (n != null) {
    const taux = tauxTvaEnVigueur(soc);
    const type = TYPES_TVA.find((t) => taux[t.cle] === n);
    if (type) return type.cle;
  }
  return 'normal';
}

/**
 * Taux de TVA d'un nouveau document, à sa date.
 *
 * @param {object} [soc] entreprise
 * @param {Date|string} [date] date du document ; aujourd'hui par défaut
 * @returns {number} le taux, en pour-cent
 */
export function tauxTvaParDefaut(soc, date) {
  const type = typeTvaDefaut(soc);
  return type === 'exonere' ? 0 : tauxTvaEnVigueur(soc, date)[type];
}

/**
 * Les taux proposés pour une pièce à une date : normal, réduit, hébergement
 * et toujours 0 % (exonéré), sans doublon.
 *
 * @param {object} [soc] entreprise
 * @param {Date|string} [date] date de la pièce ; aujourd'hui par défaut
 * @returns {number[]} les taux
 */
export function tauxTvaProposes(soc, date) {
  const taux = tauxTvaEnVigueur(soc, date);
  return [taux.normal, taux.reduit, taux.hebergement, 0].filter((v, i, a) => a.indexOf(v) === i);
}

/**
 * Écriture d'un taux, « 8.1 % », comme les documents l'impriment.
 *
 * @param {number} taux taux en pour-cent
 * @returns {string} le taux écrit
 */
export function ecritTaux(taux) {
  return `${+taux} %`;
}
